import type { CalendarFeed, CalendarItem } from "@ronin/contracts";
import { dateParts, layoutSpans, type WeekStart } from "@ronin/core";
import { cn } from "@ronin/ui";
import { Flag } from "lucide-react";
import { useMemo, useRef } from "react";
import { holidaysByDay, monthSpans } from "../../lib/calendar-items.js";
import { longDate, weekdayLabels } from "../../lib/calendar-view.js";
import { useElementHeight } from "../../lib/use-element-height.js";
import { ItemChip } from "./item-chip.js";
import { describeItem } from "./item-style.js";

/** Row geometry in px: date header (number + holiday label), event lanes, "+n more". */
const HEADER = 40;
const LANE = 22;
const MORE = 18;

interface MonthViewProps {
  date: string;
  days: readonly string[];
  items: readonly CalendarItem[];
  feeds: ReadonlyMap<string, CalendarFeed>;
  timeZone: string;
  today: string;
  weekStart: WeekStart;
  /** Phones show at most 2 entries per day. */
  compact: boolean;
  onCreate: (date: string) => void;
  onOpenItem: (item: CalendarItem) => void;
  onOpenDay: (date: string) => void;
}

/**
 * Month grid (spec03 FR3): always 6 weeks, so the layout never jumps. Multi-day events are
 * bars across the days of a week; more than 3 (2 on phones) entries show "+n more".
 */
export function MonthView({
  date,
  days,
  items,
  feeds,
  timeZone,
  today,
  weekStart,
  compact,
  onCreate,
  onOpenItem,
  onOpenDay,
}: MonthViewProps) {
  // The grid fills the calendar box; as many entries per day as fit (at least 1, 2 on phones).
  const bodyRef = useRef<HTMLDivElement>(null);
  const bodyHeight = useElementHeight(bodyRef);
  const rowHeight = bodyHeight / 6;
  const fitting = Math.max(1, Math.floor((rowHeight - HEADER - MORE) / LANE));
  const maxLanes = compact ? Math.min(2, fitting) : fitting;
  const month = dateParts(date).month;
  const spans = useMemo(() => monthSpans(items, timeZone), [items, timeZone]);
  const holidays = useMemo(() => holidaysByDay(items, days), [items, days]);
  const weeks = Array.from({ length: days.length / 7 }, (_, i) => days.slice(i * 7, i * 7 + 7));

  return (
    <div className="flex h-full flex-col border bg-card">
      <div className="grid grid-cols-7 border-b" aria-hidden>
        {weekdayLabels(weekStart).map((label) => (
          <div
            key={label}
            className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-dim-foreground"
          >
            {label}
          </div>
        ))}
      </div>

      <div ref={bodyRef} className="grid min-h-0 flex-1 grid-rows-6">
        {weeks.map((week) => {
          const weekStartDate = week[0] ?? "";
          const placed = layoutSpans(spans, weekStartDate, 7);
          const shown = placed.filter((p) => p.lane < maxLanes);
          const hiddenPerDay = week.map(
            (_, column) =>
              placed.filter(
                (p) => p.lane >= maxLanes && p.column <= column && p.column + p.length > column,
              ).length,
          );
          return (
            <div
              key={weekStartDate}
              className="relative grid min-h-0 grid-cols-7 overflow-hidden border-b last:border-b-0"
            >
              {week.map((day, column) => {
                const dayHolidays = holidays.get(day);
                const publicHoliday = dayHolidays?.public[0];
                const school = dayHolidays?.school[0];
                const outside = dateParts(day).month !== month;
                const isToday = day === today;
                const hidden = hiddenPerDay[column] ?? 0;
                return (
                  // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: a click on empty space is a mouse shortcut; the keyboard uses "New event" (N)
                  <div
                    key={day}
                    onClick={() => onCreate(day)}
                    className={cn(
                      "relative min-w-0 cursor-pointer border-l px-1 first:border-l-0 hover:bg-muted/20",
                      school && "border-t-2 border-t-faint-foreground/40",
                    )}
                  >
                    <div className="flex h-6 items-center gap-1.5 pt-1">
                      <button
                        type="button"
                        aria-label={`${longDate(day)}${isToday ? ", today" : ""}: open day`}
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenDay(day);
                        }}
                        className={cn(
                          "flex h-5 min-w-5 items-center justify-center px-1 text-xs font-medium tabular-nums hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring",
                          outside ? "text-faint-foreground" : "text-secondary-foreground",
                          isToday && "bg-primary text-primary-foreground hover:bg-primary-hover",
                        )}
                      >
                        {dateParts(day).day}
                      </button>
                      {school && (column === 0 || school.startDate === day) && (
                        <span className="truncate text-[10px] text-faint-foreground">
                          {school.title}
                        </span>
                      )}
                    </div>
                    {publicHoliday && (
                      <p className="flex h-4 items-center gap-1 truncate px-1 text-xs text-muted-foreground">
                        <Flag className="size-3 shrink-0" aria-hidden />
                        <span className="truncate">{publicHoliday.title}</span>
                      </p>
                    )}
                    {hidden > 0 && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenDay(day);
                        }}
                        className="absolute right-1 bottom-0.5 left-1 h-4 truncate text-left text-[11px] text-dim-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
                      >
                        +{hidden} more
                      </button>
                    )}
                  </div>
                );
              })}

              {shown.map((p) => (
                <ItemChip
                  key={p.span.id}
                  item={p.span.item}
                  feeds={feeds}
                  time={compact ? null : p.span.time}
                  clippedStart={p.clippedStart}
                  clippedEnd={p.clippedEnd}
                  label={describeItem(p.span.item, timeZone, feeds)}
                  onOpen={onOpenItem}
                  className={cn(
                    "absolute",
                    dateParts(p.span.startDate).month !== month && "opacity-60",
                  )}
                  style={{
                    top: HEADER + p.lane * LANE,
                    left: `calc(${(p.column / 7) * 100}% + 2px)`,
                    width: `calc(${(p.length / 7) * 100}% - 4px)`,
                  }}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
